// Copyright 2021 Sorama B.V.

import { IIndexableObject } from "libraries/IndexedDb/IIndexableObject";
import { IndexedDb } from "libraries/IndexedDb/IndexedDb";
import { ILocalStorageRepository } from "./ILocalStorageRepository";

interface Base64EncodedUriItem extends IIndexableObject {
    data: string;
}

export abstract class Base64UriLocalStorageRepository implements ILocalStorageRepository<string> {
    private static readonly base64UriRegex = /data:([a-zA-Z0-9]+\/[a-zA-Z0-9-]+);base64,/;
    protected abstract readonly storeName: string;

    constructor(private readonly IndexedDb: IndexedDb) {}

    public async get(key: string): Promise<string> {
        const object = await this.IndexedDb.getObject<Base64EncodedUriItem>(key, this.storeName);
        return object.data;
    }

    public set(key: string, value: string): Promise<void> {
        if (!Base64UriLocalStorageRepository.base64UriRegex.test(value)) {
            return Promise.reject(new Error("Invalid base64 encoded URI."));
        }

        return this.IndexedDb.addObject<Base64EncodedUriItem>(
            { id: key, data: value, userId: undefined },
            this.storeName
        ).then((indexKey) => {
            if (indexKey !== key) {
                return this.delete(indexKey)
                    .catch(() => {})
                    .then(() => {
                        throw new Error("Unable to store object in raw storage, key in use.");
                    });
            }
        });
    }

    public delete(key: string): Promise<void> {
        return this.IndexedDb.removeObject(key, this.storeName);
    }
}
